import PropTypes from 'prop-types';
import Platform from "react-native-web/dist/exports/Platform";
import getPropSelector from './getPropSelector';

// This file contains common props for components that render a React Native TextInput
// It excludes interaction handler functions which are in `./handlerProps.js`

/**
 * TextInput (web and native) supports some common React Native props
 * shared with React Native's Text component.
 */
const textProps = {
  maxFontSizeMultiplier: PropTypes.number,
  nativeId: PropTypes.string,
  onLayout: PropTypes.func
};

/**
 * UDS text inputs accept props related to UDS's useInputValue hook
 */
const inputValueProps = {
  value: PropTypes.string,
  type: PropTypes.string,
  initialValue: PropTypes.string,
  readOnly: PropTypes.bool,
  inactive: PropTypes.bool
};

/**
 * This collection adds props that can be passed through to both React Native's
 * and React Native Web's implementations of the React Native TextInput component.
 */
const crossPlatform = {
  ...textProps,
  ...inputValueProps,
  /**
   * Web and Android; 'off' disables device autocomplete, other strings are platform-specific.
   * Valid values on Native: https://reactnative.dev/docs/textinput#autocomplete-android
   * Valid values on Web: https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
   */
  autoComplete: PropTypes.string,
  /**
   * On Native, default is `true`, passing `false` disables autoCorrect.
   * On web, only supported by Safari and expects "on" or "off" strings.
   */
  autoCorrect: PropTypes.oneOf([true, false, 'on', 'off']),
  /**
   * Focuses the element on mount. On Web, only the first form element with autoFocus is focussed.
   */
  autoFocus: PropTypes.bool,
  /**
   * Default is `true` for single line, `false` for multi-line
   */
  blurOnSubmit: PropTypes.bool,
  /**
   * iOS and Web only, no effect on Android
   */
  clearTextOnFocus: PropTypes.bool,
  /**
   * Default is `true`. On web, this works by mapping to input's `readonly` attribute
   */
  editable: PropTypes.bool,
  /**
   * See documentation for allowed values (varies between Web, Android and iOS) and important notes:
   * - Native: https://reactnative.dev/docs/textinput#keyboardtype
   * - Web: equivalent to `inputmode` but see https://necolas.github.io/react-native-web/docs/text-input/
   */
  keyboardType: PropTypes.string,
  /**
   * Uses native tools (no flicker) to cap the maximum number of characters.
   * On Web, works via `maxlength` attr.
   */
  maxLength: PropTypes.number,
  /**
   * If passed as true, the text input can be multiple lines.
   *
   * > It is important to note that this aligns the text to the top on iOS, and centers it on Android.
   * > Use with textAlignVertical set to top for the same behavior in both platforms.
   */
  multiline: PropTypes.bool,
  /**
   * Web and Android only, requires `multiline` to be `true`.
   */
  numberOfLines: PropTypes.number,
  /**
   * Text to display when no value set.
   * Accesibility guidelines recommend using labels to describe the input and using
   * placeholders rarely and sparingly to prompt a particular format.
   */
  placeholder: PropTypes.string,
  /**
   * Sets placeholder colour. On Web, uses `::placeholder { color: ... }` selector.
   */
  placeholderTextColor: PropTypes.string,
  /**
   * One of a subset of platform-specific options that controls labelling and presentation
   * in on-screen keyboards and accessibility tools. Uses `enterkeyhint` attr on Web.
   *
   * 'done', 'go', 'next', 'search', and 'send' are available for Web, Android and iOS.
   */
  returnKeyType: PropTypes.string,
  /**
   * Obscures passwords and similar. Equivalent to type="password" on Web.
   * Does not work if multiline is true.
   */
  secureTextEntry: PropTypes.bool,
  /**
   * If true, all text will automatically be selected on focus.
   */
  selectTextOnFocus: PropTypes.bool,
  /**
   * Web and iOS. On iOS, default inherits from `autoCorrect`.
   * On Web, equivalent to `spellcheck` attr.
   */
  spellCheck: PropTypes.bool
};

/**
 * These web-only props all control HTML `input` attributes of the same name.
 * Refer to general HTML documentation for more details.
 */
const webOnly = {
  disabled: PropTypes.bool,
  dir: PropTypes.oneOf(['auto', 'ltr', 'rtl']),
  lang: PropTypes.string,
  /**
   * Sets the HTML input `pattern` attr. Not supported by React Native Web, but is supported by UDS.
   * Must also pass in a ref and check validity by calling the HTML element's checkValidity method:
   * https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/checkValidity
   */
  pattern: PropTypes.string
};

/**
 * These props are supported in React Native but not React Native Web.
 *
 * React Native text inputs can be quirky, so a full set of props should be allowed to handle edge cases.
 * Refer to React Native documentation for details, allowed values, and Android/iOS support and versions:
 * https://reactnative.dev/docs/textinput
 *
 * Beware that many React Native text input props apply via complicated logic that chooses a built-in
 * native component based on the values of multiple boolean flags, and may sometimes appear to pick an
 * option that is inappropriate for one flag based on the values of one or more other other flags.
 */
const nativeOnly = {
  caretHidden: PropTypes.bool,
  clearButtonMode: PropTypes.string,
  contextMenuHidden: PropTypes.bool,
  dataDetectorTypes: PropTypes.string,
  disableFullscreenUI: PropTypes.bool,
  enablesReturnKeyAutomatically: PropTypes.bool,
  importantForAutofill: PropTypes.string,
  inlineImageLeft: PropTypes.string,
  keyboardAppearance: PropTypes.string,
  returnKeyLabel: PropTypes.string,
  rejectResponderTermination: PropTypes.bool,
  scrollEnabled: PropTypes.bool,
  selection: PropTypes.object,
  selectionColor: PropTypes.string,
  showSoftInputOnFocus: PropTypes.bool,
  textAlign: PropTypes.string,
  textContentType: PropTypes.string,
  passwordRules: PropTypes.string,
  textBreakStrategy: PropTypes.string,
  underlineColorAndroid: PropTypes.string
};
export default {
  /**
   * Subset of proptypes that can be passed down to a React Native or React Native Web
   * `TextInput` component. Allow regardless of platform, so cross-platform apps don't warn.
   */
  types: {
    ...crossPlatform,
    ...webOnly,
    ...nativeOnly
  },
  /**
   * Filters a props object. Return only platform-appropriate TextInput props, native inputs
   * may throw errors on receiving unknown props.
   */
  select: getPropSelector({
    ...crossPlatform,
    ...Platform.select({
      web: webOnly,
      default: nativeOnly
    })
  })
};